import { ref, computed, watch } from 'vue'
import * as Sentry from '@sentry/vue'
import type { EditorSettings } from '../types/editor'
import { DEFAULT_EDITOR_SETTINGS } from '../types/editor'
import EditorSettingsService from '../services/editorSettings'

const STORAGE_KEY = 'escribonauta-editor-settings'

const settings = ref<EditorSettings>({ ...DEFAULT_EDITOR_SETTINGS })
const isLoading = ref(false)
const isError = ref(false)
const errorMessage = ref('')

type SettingsOperation = 'load' | 'save' | 'patch' | 'reset'

const pickVisualSettings = (value: EditorSettings) => ({
  fontSize: value.fontSize,
  fontFamily: value.fontFamily,
  pageWidth: value.pageWidth,
  lineHeight: value.lineHeight,
  indentSize: value.indentSize,
  paragraphSpacing: value.paragraphSpacing,
})

const captureEditorSettingsError = (
  error: unknown,
  operation: SettingsOperation,
  extra: Record<string, unknown> = {},
) => {
  Sentry.withScope((scope) => {
    scope.setTag('feature', 'editor_settings')
    scope.setTag('layer', 'composable')
    scope.setTag('operation', operation)
    Object.entries(extra).forEach(([key, value]) => {
      scope.setExtra(key, value)
    })
    Sentry.captureException(error)
  })
}

const initializeFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsedSettings = JSON.parse(stored)
      settings.value = { ...DEFAULT_EDITOR_SETTINGS, ...parsedSettings }
    }
  } catch (error) {
    console.error('Error loading settings from localStorage:', error)
    settings.value = { ...DEFAULT_EDITOR_SETTINGS }
  }
}

const saveToStorage = (newSettings: EditorSettings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings))
  } catch (error) {
    console.error('Error saving settings to localStorage:', error)
  }
}

watch(
  settings,
  (newSettings) => {
    saveToStorage(newSettings)
  },
  { deep: true },
)

export function resetLocalEditorSettingsToDefaults() {
  settings.value = { ...DEFAULT_EDITOR_SETTINGS }
}

export const useEditorSettings = () => {
  if (JSON.stringify(settings.value) === JSON.stringify(DEFAULT_EDITOR_SETTINGS)) {
    initializeFromStorage()
  }

  const loadSettings = async () => {
    isLoading.value = true
    isError.value = false
    errorMessage.value = ''

    const localVisualSettings = pickVisualSettings(settings.value)

    try {
      const response = await EditorSettingsService.getSettings()
      const serverSettings = { ...DEFAULT_EDITOR_SETTINGS, ...response.data }

      if (settings.value.theme !== serverSettings.theme) {
        settings.value = serverSettings
        saveToStorage(serverSettings)
      } else if (JSON.stringify(settings.value) !== JSON.stringify(serverSettings)) {
        settings.value = serverSettings
      }
    } catch (error) {
      // Keep local settings on load failure; do not reset to defaults
      isError.value = true
      errorMessage.value = error instanceof Error ? error.message : 'Error loading settings'
      console.error('Error loading settings from server:', error)
      captureEditorSettingsError(error, 'load', {
        localVisualSettings,
      })
    } finally {
      isLoading.value = false
    }
  }

  const saveSettings = async (newSettings: EditorSettings) => {
    isLoading.value = true
    isError.value = false
    errorMessage.value = ''

    try {
      const response = await EditorSettingsService.saveSettings(newSettings)
      settings.value = response.data
      return true
    } catch (error) {
      isError.value = true
      errorMessage.value = error instanceof Error ? error.message : 'Error saving settings'
      console.error('Error saving settings to server:', error)
      captureEditorSettingsError(error, 'save', {
        localVisualSettings: pickVisualSettings(settings.value),
        attemptedVisualSettings: pickVisualSettings(newSettings),
      })
      return false
    } finally {
      isLoading.value = false
    }
  }

  const patchSettings = async (partialSettings: Partial<EditorSettings>) => {
    isLoading.value = true
    isError.value = false
    errorMessage.value = ''

    try {
      const response = await EditorSettingsService.patchSettings(partialSettings)
      const mismatchedKeys = (Object.keys(partialSettings) as (keyof EditorSettings)[]).filter(
        (key) => response.data[key] !== partialSettings[key],
      )

      if (mismatchedKeys.length > 0) {
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'editor_settings')
          scope.setTag('layer', 'composable')
          scope.setTag('operation', 'patch')
          scope.setLevel('warning')
          scope.setExtra('mismatchedKeys', mismatchedKeys)
          scope.setExtra('sent', partialSettings)
          scope.setExtra(
            'received',
            Object.fromEntries(mismatchedKeys.map((key) => [key, response.data[key]])),
          )
          scope.setExtra('localVisualSettings', pickVisualSettings(settings.value))
          Sentry.captureMessage('Editor settings patch response mismatch')
        })
      }

      const patchedFields = Object.fromEntries(
        Object.keys(partialSettings).map((key) => [
          key,
          response.data[key as keyof EditorSettings],
        ]),
      ) as Partial<EditorSettings>

      Object.assign(settings.value, patchedFields)
      return true
    } catch (error) {
      isError.value = true
      errorMessage.value = error instanceof Error ? error.message : 'Error updating settings'
      console.error('Error updating settings on server:', error)
      captureEditorSettingsError(error, 'patch', {
        keys: Object.keys(partialSettings),
        partialSettings,
        localVisualSettings: pickVisualSettings(settings.value),
      })
      return false
    } finally {
      isLoading.value = false
    }
  }

  const updateSetting = <K extends keyof EditorSettings>(key: K, value: EditorSettings[K]) => {
    settings.value[key] = value
  }

  const updateAndSaveSetting = async <K extends keyof EditorSettings>(
    key: K,
    value: EditorSettings[K],
  ) => {
    const oldValue = settings.value[key]
    settings.value[key] = value

    const success = await patchSettings({ [key]: value } as Partial<EditorSettings>)

    if (!success) {
      settings.value[key] = oldValue
    }

    return success
  }

  const resetSettings = async () => {
    isLoading.value = true
    isError.value = false
    errorMessage.value = ''

    const localVisualSettings = pickVisualSettings(settings.value)

    try {
      const response = await EditorSettingsService.resetSettings()
      settings.value = response.data
      return true
    } catch (error) {
      isError.value = true
      errorMessage.value = error instanceof Error ? error.message : 'Error resetting settings'
      console.error('Error resetting settings:', error)
      captureEditorSettingsError(error, 'reset', {
        localVisualSettings,
      })
      return false
    } finally {
      isLoading.value = false
    }
  }

  const fontSize = computed(() => settings.value.fontSize)
  const fontFamily = computed(() => settings.value.fontFamily)
  const lineHeight = computed(() => settings.value.lineHeight)
  const pageWidth = computed(() => settings.value.pageWidth)
  const autoSave = computed(() => settings.value.autoSave)
  const autoSaveFrequency = computed(() => settings.value.autoSaveFrequency)
  const theme = computed(() => settings.value.theme)
  const showWordCount = computed(() => settings.value.showWordCount)
  const spellCheck = computed(() => settings.value.spellCheck)
  const indentSize = computed(() => settings.value.indentSize)
  const paragraphSpacing = computed(() => settings.value.paragraphSpacing)
  const hideSaveStatus = computed(() => settings.value.hideSaveStatus)
  const hideCountStats = computed(() => settings.value.hideCountStats)
  const showMobileSaveStatus = computed(() => settings.value.showMobileSaveStatus)
  const showMobileCountStats = computed(() => settings.value.showMobileCountStats)
  const showMobileWritingSession = computed(() => settings.value.showMobileWritingSession)
  return {
    settings: computed(() => settings.value),
    isLoading: computed(() => isLoading.value),
    isError: computed(() => isError.value),
    errorMessage: computed(() => errorMessage.value),
    fontSize,
    fontFamily,
    lineHeight,
    pageWidth,
    autoSave,
    autoSaveFrequency,
    theme,
    showWordCount,
    spellCheck,
    indentSize,
    paragraphSpacing,
    hideSaveStatus,
    hideCountStats,
    showMobileSaveStatus,
    showMobileCountStats,
    showMobileWritingSession,
    loadSettings,
    saveSettings,
    patchSettings,
    updateSetting,
    updateAndSaveSetting,
    resetSettings,
  }
}
